"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { formatBytes } from "@/lib/tool-utils";

const MAX_FILE_BYTES = 25 * 1024 * 1024; // 25 MB
const DEFAULT_STATUS = "Paste text or drop a file to convert it to Base64.";

type Mode = "encode" | "decode";

/** UTF-8 safe encode: plain btoa() throws on emoji and non-Latin text. */
function utf8ToBase64(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = "";
  const chunk = 0x8000; // chunked to avoid call-stack limits on large input
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/** Accepts whitespace, data-URL prefixes, Base64URL (- and _) and missing padding. */
function base64ToUtf8(value: string): string {
  let cleaned = value
    .trim()
    .replace(/^data:[^,]*;base64,/i, "")
    .replace(/\s+/g, "")
    .replace(/-/g, "+")
    .replace(/_/g, "/");

  if (!/^[A-Za-z0-9+/]*={0,2}$/.test(cleaned)) {
    throw new Error("This doesn't look like valid Base64. Check for stray characters or quotes.");
  }

  while (cleaned.length % 4 !== 0) cleaned += "=";

  const binary = atob(cleaned);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
}

export default function Base64Tool({}: { toolName: string }) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const copyTimer = useRef<number | undefined>(undefined);

  const [mode, setMode] = useState<Mode>("encode");
  const [input, setInput] = useState("");
  const [fileOutput, setFileOutput] = useState(""); // data URL from a dropped file
  const [fileName, setFileName] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [status, setStatus] = useState(DEFAULT_STATUS);
  const [error, setError] = useState("");
  const [copyLabel, setCopyLabel] = useState("Copy output");

  // Output is derived from the input, so it can never fall out of sync.
  // A dropped file takes priority until the user types or clears.
  let output = "";
  let derivedError = "";
  if (fileOutput) {
    output = fileOutput;
  } else if (input) {
    try {
      output = mode === "encode" ? utf8ToBase64(input) : base64ToUtf8(input);
    } catch (err) {
      derivedError =
        err instanceof Error && err.message.includes("valid Base64")
          ? err.message
          : "Couldn't decode this. It may be incomplete, or it may be binary data rather than text.";
    }
  }
  const visibleError = error || derivedError;

  useEffect(() => {
    return () => window.clearTimeout(copyTimer.current);
  }, []);

  const handleFile = useCallback((file: File) => {
    if (file.size > MAX_FILE_BYTES) {
      setError(`That file is ${formatBytes(file.size)}. The limit is ${formatBytes(MAX_FILE_BYTES)}.`);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      setMode("encode");
      setInput("");
      setFileOutput(result);
      setFileName(file.name);
      setError("");
      setStatus(`Encoded ${file.name} (${formatBytes(file.size)}) as a Base64 data URL.`);
    };
    reader.onerror = () => setError("The selected file could not be read. Please try again.");
    reader.readAsDataURL(file);
  }, []);

  function switchMode(next: Mode) {
    setMode(next);
    setFileOutput("");
    setFileName("");
    setError("");
    setStatus(
      next === "encode" ? DEFAULT_STATUS : "Paste Base64 to decode it back to text.",
    );
  }

  function handleInputChange(value: string) {
    setFileOutput("");
    setFileName("");
    setError("");
    setInput(value);
    setStatus(mode === "encode" ? DEFAULT_STATUS : "Paste Base64 to decode it back to text.");
  }

  function clearAll() {
    setInput("");
    setFileOutput("");
    setFileName("");
    setError("");
    setStatus("Cleared. Paste text or drop a file to start again.");
  }

  async function copyOutput() {
    if (!output) return;
    window.clearTimeout(copyTimer.current);
    try {
      await navigator.clipboard.writeText(output);
      setCopyLabel("Copied!");
    } catch {
      setCopyLabel("Copy failed");
    }
    copyTimer.current = window.setTimeout(() => setCopyLabel("Copy output"), 1500);
  }

  function useOutputAsInput() {
    setMode("encode");
    setFileOutput("");
    setFileName("");
    setError("");
    setInput(output);
    setStatus("Output moved to the input box.");
  }

  const isEncode = mode === "encode";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Base64 mode">
        {(["encode", "decode"] as const).map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={mode === value}
            onClick={() => switchMode(value)}
            className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
              mode === value
                ? "bg-indigo-600 text-white"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            {value === "encode" ? "Encode" : "Decode"}
          </button>
        ))}
      </div>

      <div
        role="button"
        tabIndex={0}
        aria-label="Choose a file to encode as Base64, or drag one here"
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            fileInputRef.current?.click();
          }
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragActive(false);
          const file = event.dataTransfer.files?.[0];
          if (file) handleFile(file);
        }}
        className={`cursor-pointer rounded-2xl border-2 border-dashed p-5 text-center outline-none transition focus-visible:ring-2 focus-visible:ring-indigo-300 ${
          dragActive
            ? "border-indigo-500 bg-indigo-50"
            : "border-slate-300 bg-slate-50 hover:border-indigo-400"
        }`}
      >
        <p className="text-sm font-semibold text-slate-900">
          {fileName ? `Loaded: ${fileName}` : "Drag a file here, or click to browse"}
        </p>
        <p className="mt-1 text-sm text-slate-600">
          Any file type up to {formatBytes(MAX_FILE_BYTES)}. It becomes a Base64 data URL in the output box.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) handleFile(file);
            event.target.value = "";
          }}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <label className="text-sm font-medium text-slate-700">
          {isEncode ? "Input text" : "Base64 input"}
          <textarea
            value={input}
            onChange={(event) => handleInputChange(event.target.value)}
            rows={10}
            spellCheck={false}
            aria-invalid={Boolean(visibleError)}
            className={`mt-2 w-full rounded-lg border px-3 py-2 font-mono text-sm outline-none transition focus:ring-2 ${
              visibleError
                ? "border-rose-400 focus:ring-rose-200"
                : "border-slate-300 focus:border-indigo-500 focus:ring-indigo-200"
            }`}
            placeholder={
              isEncode
                ? "Type or paste text to encode (UTF-8 safe)."
                : "Paste Base64 text to decode."
            }
          />
        </label>

        <label className="text-sm font-medium text-slate-700">
          {isEncode ? "Base64 output" : "Decoded output"}
          <textarea
            value={output}
            readOnly
            rows={10}
            spellCheck={false}
            className="mt-2 w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm"
            placeholder={
              isEncode ? "Base64 result will appear here." : "Decoded text will appear here."
            }
          />
        </label>
      </div>

      {visibleError && (
        <p
          role="alert"
          className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
        >
          {visibleError}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => void copyOutput()}
          disabled={!output}
          className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          {copyLabel}
        </button>

        {!isEncode && output && (
          <button
            type="button"
            onClick={useOutputAsInput}
            className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-200"
          >
            Use output as input
          </button>
        )}

        <button
          type="button"
          onClick={clearAll}
          disabled={!input && !fileOutput}
          className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Clear
        </button>

        <span
          aria-live="polite"
          className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700"
        >
          {status}
        </span>
      </div>
    </div>
  );
}